import {
  CheckCircleIcon,
  InfoIcon,
  WarningCircleIcon,
  WarningIcon,
  type Icon,
} from "@phosphor-icons/react"
import type { HTMLAttributes, ReactNode } from "react"

import {
  LabelledActionButton,
  type LabelledAction,
} from "~/components/reusable/LabelledActionButton"
import { cn } from "~/utils/helpers"

export type InlineAlertTone = "neutral" | "info" | "success" | "warning" | "destructive"
export type InlineAlertSize = "compact" | "block"

export type InlineAlertAction = LabelledAction

// Badge tokens only: the box and the icon carry the tone, the message never does.
const TONE: Record<InlineAlertTone, { box: string; icon: string; Icon: Icon }> = {
  neutral: {
    box: "bg-badge-neutral-bg border-badge-neutral-border",
    icon: "text-badge-neutral-fg-text",
    Icon: InfoIcon,
  },
  info: {
    box: "bg-badge-blue-bg border-badge-blue-border",
    icon: "text-badge-blue-fg-text",
    Icon: InfoIcon,
  },
  success: {
    box: "bg-badge-green-bg border-badge-green-border",
    icon: "text-badge-green-fg-text",
    Icon: CheckCircleIcon,
  },
  warning: {
    box: "bg-badge-orange-bg border-badge-orange-border",
    icon: "text-badge-orange-fg-text",
    Icon: WarningIcon,
  },
  destructive: {
    box: "bg-badge-red-bg border-badge-red-border",
    icon: "text-badge-red-fg-text",
    Icon: WarningCircleIcon,
  },
}

/**
 * Inline message about what the user is looking at: five tones, `compact` (one line) or `block`
 * (title over description), up to two actions on the right. Defaults: neutral, compact, icon on.
 * Canonical intent: {@link ../design-system/intents.ts INLINE_ALERT_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export const InlineAlert = ({
  tone = "neutral",
  size = "compact",
  title,
  description,
  showIcon = true,
  action,
  secondaryAction,
  className,
  ...props
}: {
  tone?: InlineAlertTone
  size?: InlineAlertSize
  title?: ReactNode
  description?: ReactNode
  showIcon?: boolean
  action?: InlineAlertAction
  secondaryAction?: InlineAlertAction
  className?: string
} & Omit<HTMLAttributes<HTMLDivElement>, "title">) => {
  if (!title && !description) return null
  const { box, icon, Icon } = TONE[tone]
  const isBlock = size === "block"
  return (
    <div
      className={cn(
        // Wraps so the actions drop below the icon and message when both no longer fit.
        "flex w-full flex-wrap gap-2 rounded-[8px] border",
        isBlock ? "items-start px-4 py-3" : "items-center px-3 py-2",
        box,
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "flex min-w-0 flex-[1_1_240px] gap-2",
          isBlock ? "items-start" : "items-center",
        )}
      >
        {showIcon && (
          // Block: nudged so the 16px icon centres on the 20px title line.
          <Icon size={16} aria-hidden className={cn("shrink-0", icon, isBlock && "mt-0.5")} />
        )}
        <div
          className={cn(
            "flex min-w-0 flex-1",
            isBlock ? "flex-col gap-0.5" : "flex-wrap items-baseline gap-x-1.5",
          )}
        >
          {title && (
            <span
              className={cn(
                "min-w-0 text-fg-base wrap-anywhere",
                isBlock ? "label-base" : "label-sm",
              )}
            >
              {title}
            </span>
          )}
          {description && (
            <span
              className={cn(
                "min-w-0 text-fg-subtle wrap-anywhere",
                isBlock ? "paragraph-base" : "paragraph-sm",
              )}
            >
              {description}
            </span>
          )}
        </div>
      </div>
      {(action || secondaryAction) && (
        <div className="ml-auto flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2">
          {secondaryAction && (
            <LabelledActionButton action={secondaryAction} variant="transparent" />
          )}
          {action && <LabelledActionButton action={action} variant="neutral" />}
        </div>
      )}
    </div>
  )
}
